import { Fragment, useState, type ComponentType } from "react";
import {
  AlertTriangle,
  ArrowRight,
  Clock,
  ChevronDown,
  FileQuestion,
  FileSearch,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import Loading from "@/components/Loading";
import { cn } from "@/lib/utils";
import type { Credential } from "../types";
import { blankCredentialInstance } from "../lib/blank-credential-instance";
import { credentialReviewTarget } from "../lib/credential-review-target";
import { DeleteUploadDialog } from "./delete-upload-dialog";
import { PROFILE_FORM_STYLES } from "@/components/Forms/profile-form-styles";
import {
  BLOCKED_UPLOAD_DESCRIPTION,
  BLOCKED_UPLOAD_LABEL,
  isReviewUploadBlocked,
  type ReviewUploadScan,
} from "../lib/review-upload-security";

/**
 * The uploads in flight, as a queue of work rather than a notice.
 *
 * Pending uploads used to live only inside one credential's edit modal, which
 * meant the one thing needing a decision was the one thing you had to already
 * know about to find. Someone dropping a file could close the page and never
 * learn it had finished reading, and nothing on the page said a review was
 * waiting.
 *
 * **An upload is not finished until someone reviews it, and this has to say so.**
 * Earlier passes read as an informational banner — a tinted box listing file
 * names, where the required action was a quiet link or, worse, a document name
 * that looked like plain text. People uploaded files and left, believing the work
 * was done. The obligation is now stated in words ("nothing is saved until you
 * review"), carried by the only coloured chip in the list, and attached to a real
 * button on every row. Clicking the name shows the file, the same as Preview
 * does, but nobody has to discover that to get the job done.
 *
 * This remains a pointer, not a second review surface: the decision belongs next
 * to the credential's own values, where a reviewer can see what they are about to
 * change. Each row opens that modal rather than trying to resolve in place.
 */

type PendingState =
  | "reading"
  | "stalled"
  | "unreadable"
  | "nothing-found"
  | "needs-review";

type PendingItem = ReviewUploadScan & {
  documentId: string;
  title: string;
  fileName: string;
  state: PendingState;
  targetCredentialId: string | null;
  candidate: {
    credentialType: string;
    matchedCredentialId: string | null;
  } | null;
};

/**
 * One chip vocabulary for the upload pipeline, deliberately separate from
 * `StatusChip`'s provenance vocabulary — an upload's progress and a credential's
 * standing are different axes and must never collapse into one word.
 *
 * OCR progress is separate from a security block, which overrides this chip
 * in `StateChip`. Each state has text and an icon as well as colour.
 */
const STATE_CHIP: Record<
  PendingState,
  {
    label: string;
    classes: string;
    icon: ComponentType<{ className?: string }> | null;
  }
> = {
  "needs-review": {
    label: "Needs review",
    classes: "border-amber-300 bg-amber-50 text-amber-700",
    icon: FileSearch,
  },
  reading: {
    label: "Reading…",
    classes: "border-slate-300 bg-slate-50 text-slate-600",
    // The spinner IS the icon here; a static glyph beside it would double up.
    icon: null,
  },
  stalled: {
    label: "Not read",
    classes: "border-slate-300 bg-slate-50 text-slate-600",
    icon: Clock,
  },
  unreadable: {
    label: "Couldn't read",
    classes: "border-slate-300 bg-slate-50 text-slate-600",
    icon: AlertTriangle,
  },
  "nothing-found": {
    label: "Nothing found",
    classes: "border-slate-300 bg-slate-50 text-slate-600",
    icon: FileQuestion,
  },
};

/**
 * States a reader can act on from this list: file it elsewhere, or delete it.
 *
 * Every landed upload, whatever the parse made of it. An identified file used to
 * offer only "Review", on the reasoning that its values deserved a decision —
 * but the two things a reviewer most often knows on sight are "that is not this
 * credential" and "that should not have been sent at all", and both were
 * reachable only for the files we had failed to read. Recognising a document
 * correctly should not take away the ways of dealing with it.
 *
 * Only `reading` is excluded: acting on a file mid-parse races a result that is
 * about to land. A stalled row is the same file after the promise of progress
 * stopped being true, and that one IS actionable.
 */
const ACTIONABLE_STATES: ReadonlySet<PendingState> = new Set([
  "stalled",
  "unreadable",
  "nothing-found",
  "needs-review",
]);

/**
 * States where we cannot say what the file is — a narrower question than
 * "can the reader act on it", and the one the grouped instruction answers.
 *
 * Kept apart from `ACTIONABLE_STATES` deliberately: they were briefly the same
 * set, and an identified file then landed under "We couldn't tell what these
 * files are", which is a sentence about it that is simply untrue.
 */
const UNIDENTIFIED_STATES: ReadonlySet<PendingState> = new Set([
  "stalled",
  "unreadable",
  "nothing-found",
]);

/** One line of the "which credential is this?" picker. `isNew` entries mint a
 * further instance of a type rather than filing against one already on record. */
type PickerEntry = { card: Credential; isNew: boolean };

/**
 * The cards of one category, plus a blank entry per multi-instance type that
 * already has one on file.
 *
 * Without the blank entry, the second unrelated document of a repeatable type
 * has nowhere to go: once a provider has one "Other" on record, the picker
 * offered only that one, so filing a different file meant attaching it to an
 * unrelated credential or dismissing it and losing it. A type with nothing on
 * file yet needs no blank — its own empty card already is one.
 */
function pickerEntries(cards: Credential[]): PickerEntry[] {
  const byType = new Map<string, Credential[]>();
  for (const card of cards) {
    const existing = byType.get(card.credentialType);
    if (existing) {
      existing.push(card);
    } else {
      byType.set(card.credentialType, [card]);
    }
  }

  return [...byType.values()].flatMap((group) => {
    const entries: PickerEntry[] = group.map((card) => ({
      card,
      isNew: false,
    }));
    const [first] = group;
    if (first.allowsMultiple && group.some((card) => card.present)) {
      entries.push({
        card: blankCredentialInstance(first, "picker-new"),
        isNew: true,
      });
    }
    return entries;
  });
}

function StateChip({ item }: { item: PendingItem }) {
  const chip = isReviewUploadBlocked(item)
    ? {
        label: BLOCKED_UPLOAD_LABEL,
        classes: "border-amber-200 bg-amber-50 text-amber-800",
        icon: ShieldAlert,
      }
    : STATE_CHIP[item.state];
  const Icon = chip.icon;
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium",
        chip.classes,
      )}
    >
      {Icon ? (
        <Icon className="h-3 w-3" aria-hidden="true" />
      ) : (
        <Loading className="h-3 w-3" />
      )}
      {chip.label}
    </span>
  );
}

export function PendingUploadsNotice({
  items,
  credentials,
  onReview,
  onPreview,
  categories,
  onDelete,
  deletingDocumentId,
  appearance = "default",
  onDeleteDialogOpenChange,
}: {
  items: PendingItem[];
  /** The page's cards, for turning an upload back into the credential whose
   * modal should open. */
  credentials: Credential[];
  onReview: (credential: Credential, documentId: string) => void;
  /**
   * Show the file itself.
   *
   * OCR failures remain previewable for manual review. A security block
   * disables preview because the file cannot be served.
   */
  onPreview: (document: {
    id: string;
    title: string;
    fileName: string;
  }) => void;
  /** Category order and labels, for grouping the credential picker the way the
   * page itself groups cards. */
  categories: { slug: string; label: string }[];
  /** Destroy an upload that has nothing to decide — the file, not just its row.
   * Omitted for readers who may not write. Confirmed here before it is called. */
  onDelete?: (documentId: string) => void;
  /** The upload currently being deleted or filed, so its row can say it is
   * working rather than just going inert. */
  deletingDocumentId?: string | null;
  appearance?: "default" | "profile";
  onDeleteDialogOpenChange?: (open: boolean) => void;
}) {
  const [confirmingDelete, setConfirmingDelete] = useState<PendingItem | null>(
    null,
  );
  const isProfile = appearance === "profile";

  function changeDeleteCandidate(item: PendingItem | null) {
    setConfirmingDelete(item);
    onDeleteDialogOpenChange?.(item !== null);
  }

  if (items.length === 0) {
    return null;
  }

  const credentialFor = (item: PendingItem): Credential | null =>
    credentialReviewTarget({ item, credentials });

  /**
   * Every credential type, grouped the way the page groups its cards, plus a
   * blank entry for each type that may be held more than once.
   *
   * Deliberately not limited to credentials already on file. A file the parser
   * could not read is most often the FIRST thing on record for that credential —
   * the back of a licence nobody has entered yet — so restricting the picker to
   * existing holdings would exclude the common case. Choosing a type opens that
   * credential's own form, where the reviewer types what they can read off the
   * document and saves; the file is attached to whatever that produces.
   */
  const pickerGroups = categories
    .map((category) => ({
      label: category.label,
      entries: pickerEntries(
        credentials.filter((row) => row.category === category.slug),
      ),
    }))
    .filter((group) => group.entries.length > 0);

  const needingReview = items.filter(
    (item) => !isReviewUploadBlocked(item) && item.state === "needs-review",
  ).length;
  const reading = items.filter(
    (item) => !isReviewUploadBlocked(item) && item.state === "reading",
  ).length;
  /**
   * Every file this list is asking a person to deal with.
   *
   * The headline counts THESE, not the `needs-review` ones alone. Counting only
   * those said "1 upload needs your review" above a list of two — the parsed
   * one and a file we could not identify — and a heading that disagrees with
   * the rows beneath it is worse than no heading: whichever number the reader
   * believes, the other is a file they may never come back to.
   *
   * A file still being read is excluded on purpose: nothing is being asked of
   * anyone until it lands.
   */
  const waiting = items.length - reading;

  /**
   * What the reader is being asked for, and what happens if they walk away.
   *
   * The consequence is spelled out rather than implied. An upload that has been
   * read but not confirmed LOOKS finished — the file is stored, the page shows a
   * row — and the one thing that is not true is the thing a reviewer assumes.
   */
  const summary = () => {
    if (waiting > 0) {
      // "Review" when at least one parse is waiting on a decision, because that
      // is the bigger ask; otherwise these are files nobody could identify.
      const noun = waiting === 1 ? "upload" : "uploads";
      const verb = waiting === 1 ? "needs" : "need";
      return needingReview > 0 && !items.some(isReviewUploadBlocked)
        ? {
            headline: `${waiting} ${noun} ${verb} your review`,
            detail: "Review available files to save them to this record.",
            Icon: FileSearch,
            iconClass: "text-amber-600",
          }
        : {
            headline: `${waiting} ${noun} ${verb} attention`,
            // What to DO about them is written above the files themselves, so
            // it cannot be read as a statement about the reviewed ones too.
            detail: null,
            Icon: AlertTriangle,
            iconClass: "text-gray-400",
          };
    }
    return {
      headline:
        reading === 1 ? "Reading 1 upload" : `Reading ${reading} uploads`,
      detail:
        "This takes a few seconds. Nothing is saved until you review what we find.",
      Icon: null,
      iconClass: "",
    };
  };

  const { headline, detail, Icon, iconClass } = summary();

  /**
   * The files nobody has told us anything about, kept together under one
   * instruction.
   *
   * These rows offer three buttons and no sentence saying which of them the
   * reader is expected to press — the chip states a fact ("Nothing found")
   * rather than an obligation, and a file left alone here is simply never
   * filed. Grouping them puts the ask once, above all of them, instead of
   * repeating it per row or leaving it in a header that also has to speak for
   * the files that ARE understood.
   */
  const unknownItems = items.filter(
    (item) =>
      !isReviewUploadBlocked(item) && UNIDENTIFIED_STATES.has(item.state),
  );
  const orderedItems = [
    ...items.filter(
      (item) =>
        isReviewUploadBlocked(item) || !UNIDENTIFIED_STATES.has(item.state),
    ),
    ...unknownItems,
  ];
  const unknownInstruction =
    unknownItems.length === 1
      ? "We couldn't tell what this file is. Select the type it belongs to, or delete it."
      : "We couldn't tell what these files are. Select the type each one belongs to, or delete it.";

  return (
    <section
      aria-label="Uploads waiting for review"
      className={cn(
        "overflow-hidden rounded-xl border bg-white",
        isProfile && "rounded-2xl border-slate-200 shadow-sm",
      )}
    >
      <div
        className={cn(
          "border-b px-4 py-3",
          isProfile && "border-slate-200 px-5 py-4",
        )}
      >
        <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          {Icon ? (
            <Icon
              className={cn("h-4 w-4 shrink-0", iconClass)}
              aria-hidden="true"
            />
          ) : (
            <Loading className="h-4 w-4 shrink-0" />
          )}
          {headline}
        </h3>
        {detail && <p className="mt-1 text-xs text-gray-600">{detail}</p>}
      </div>

      <ul className="divide-y">
        {orderedItems.map((item) => {
          const isBlocked = isReviewUploadBlocked(item);
          const credential = credentialFor(item);
          const isDeleting = deletingDocumentId === item.documentId;
          /**
           * Looking at the file, and only that.
           *
           * There used to be a single "open" handler that showed the file when
           * no credential could be resolved and opened the edit modal when one
           * could — so the same button did two different things depending on
           * data the reader cannot see. Each action on this row now means one
           * thing: this one always shows the file.
           */
          const previewFile = () =>
            onPreview({
              id: item.documentId,
              title: item.title,
              fileName: item.fileName,
            });
          /** A parse waiting on a decision, with a credential to decide it
           * against. Without the credential there is nothing for the modal to
           * open onto, and picking a type is the way in instead. */
          const canReview =
            !isBlocked && item.state === "needs-review" && credential !== null;
          /** The parse worked out what this is. The row already says so — the
           * chip and the credential name beside it — so a button offering to
           * "select" a type reads as though nothing had been worked out, and
           * invites a reader to supply an answer that is already there. */
          const hasIdentifiedType = item.candidate !== null;
          // Written once, immediately above the first file it applies to.
          const opensUnknownGroup =
            item.documentId === unknownItems[0]?.documentId;

          return (
            <Fragment key={item.documentId}>
              {opensUnknownGroup && (
                <li className="bg-amber-50/60 px-4 py-2 text-xs font-medium text-amber-800">
                  {unknownInstruction}
                </li>
              )}
              <li
                className={cn(
                  "flex flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2.5",
                  isProfile && "px-5 py-4",
                )}
              >
                {/* The file name IS the preview control — it was also a button
                  for it, and two ways to do the same harmless thing crowded out
                  the two that change something. Underlined on hover so it reads
                  as clickable.

                  Full width of its own line on a phone, then shares the row
                  from `sm` up. Left to flex on narrow screens the name collapsed
                  to "N…" while a chip, a label and a button kept their space —
                  the one piece of content the reader actually needs to identify
                  the file. */}
                <button
                  type="button"
                  onClick={previewFile}
                  disabled={isBlocked}
                  title={isBlocked ? BLOCKED_UPLOAD_LABEL : "Preview this file"}
                  className="min-w-0 basis-full truncate text-left text-sm font-medium text-gray-900 underline-offset-2 enabled:hover:underline disabled:cursor-default sm:flex-1 sm:basis-auto"
                >
                  {item.title}
                </button>

                <StateChip item={item} />

                {isBlocked && (
                  <p className="basis-full text-xs leading-relaxed text-slate-600">
                    {BLOCKED_UPLOAD_DESCRIPTION}
                  </p>
                )}

                {!isBlocked && credential && item.state === "needs-review" && (
                  <span className="shrink-0 text-xs text-gray-500">
                    {credential.label}
                  </span>
                )}

                <div className="ml-auto flex shrink-0 items-center gap-1.5">
                  {canReview && (
                    <Button
                      type="button"
                      size="sm"
                      disabled={isDeleting}
                      onClick={() => onReview(credential, item.documentId)}
                      className={
                        isProfile
                          ? cn(
                              PROFILE_FORM_STYLES.primaryButton,
                              "h-9 px-3 text-xs",
                            )
                          : undefined
                      }
                    >
                      Review
                      <ArrowRight
                        className="ml-1 h-3.5 w-3.5"
                        aria-hidden="true"
                      />
                    </Button>
                  )}
                  {/* On every landed row, not just the unidentifiable ones: the
                    parse can be confident and still wrong about which
                    credential a file belongs to, and this is how someone says
                    so. It is also the only way in for a parse whose type has no
                    card on this record, where "Review" has nothing to open. */}
                  {!isBlocked && ACTIONABLE_STATES.has(item.state) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={isDeleting}
                          className={
                            isProfile
                              ? cn(
                                  PROFILE_FORM_STYLES.secondaryButton,
                                  "h-9 px-3 text-xs",
                                )
                              : undefined
                          }
                        >
                          {hasIdentifiedType ? "Change type" : "Select type"}
                          <ChevronDown
                            className="ml-1 h-3.5 w-3.5"
                            aria-hidden="true"
                          />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className={cn(
                          "max-h-80 w-72 overflow-y-auto",
                          isProfile &&
                            "max-w-[calc(100vw-3rem)] rounded-xl border-slate-200 p-2 shadow-lg",
                        )}
                      >
                        <DropdownMenuLabel className="text-xs font-normal text-gray-500">
                          Which credential is this?
                        </DropdownMenuLabel>
                        {pickerGroups.map((group) => (
                          <DropdownMenuGroup key={group.label}>
                            <DropdownMenuLabel className="text-xs text-gray-400">
                              {group.label}
                            </DropdownMenuLabel>
                            {group.entries.map(({ card, isNew }) => (
                              <DropdownMenuItem
                                key={card.id}
                                onSelect={() => onReview(card, item.documentId)}
                              >
                                <span className="min-w-0">
                                  <span className="block truncate">
                                    {isNew ? `New ${card.label}` : card.label}
                                  </span>
                                  {card.summary && (
                                    <span className="block truncate text-xs text-gray-500">
                                      {card.summary}
                                    </span>
                                  )}
                                </span>
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuGroup>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {onDelete &&
                    (isBlocked || ACTIONABLE_STATES.has(item.state)) && (
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className={cn(
                          "text-red-600 hover:text-red-700",
                          isProfile && "h-9 rounded-xl px-2 text-xs",
                        )}
                        disabled={isDeleting}
                        onClick={() => changeDeleteCandidate(item)}
                      >
                        <Trash2
                          className="mr-1.5 h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                        Delete file
                      </Button>
                    )}
                </div>
              </li>
            </Fragment>
          );
        })}
      </ul>

      {confirmingDelete && (
        <DeleteUploadDialog
          appearance={appearance}
          fileLabel={confirmingDelete.title || confirmingDelete.fileName}
          canKeepFile={!isReviewUploadBlocked(confirmingDelete)}
          isDeleting={deletingDocumentId === confirmingDelete.documentId}
          onCancel={() => changeDeleteCandidate(null)}
          onConfirm={() => {
            onDelete?.(confirmingDelete.documentId);
            changeDeleteCandidate(null);
          }}
        />
      )}
    </section>
  );
}
